/**
 * Production-only DevTools lockdown.
 *
 * Active ONLY when:
 *   - NODE_ENV === 'production'
 *   - window.self === window.top  (so the Emergent visual-editor iframe
 *     preview + local dev are unaffected)
 *
 * What it does in production:
 *   1. Neutralises the React DevTools global hook so the extension shows
 *      "Application doesn't seem to be using React" and cannot inspect
 *      the component tree.
 *   2. Silences `console.log / debug / info / trace` output. `console.error`
 *      and `console.warn` are preserved so production error telemetry
 *      still surfaces.
 *   3. Blocks common inspector shortcuts: F12, Ctrl/Cmd + Shift + I / J / C,
 *      Ctrl/Cmd + U (view-source), and the right-click context menu.
 */

const isProduction = () => process.env.NODE_ENV === 'production';
const isTopWindow = () => {
  try { return window.self === window.top; } catch { return false; }
};

const disableReactDevToolsHook = () => {
  const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
  if (typeof hook !== 'object' || hook === null) {
    // Install a no-op hook so the DevTools extension attaches to nothing.
    Object.defineProperty(window, '__REACT_DEVTOOLS_GLOBAL_HOOK__', {
      value: { isDisabled: true, supportsFiber: true, inject: () => {}, onCommitFiberRoot: () => {}, onCommitFiberUnmount: () => {} },
      writable: false,
      configurable: false,
    });
    return;
  }
  for (const key of Object.keys(hook)) {
    hook[key] = typeof hook[key] === 'function' ? () => {} : null;
  }
  hook.isDisabled = true;
};

const silenceConsole = () => {
  const noop = () => {};
  ['log', 'debug', 'info', 'trace'].forEach(method => {
    try { window.console[method] = noop; } catch { /* ignore */ }
  });
};

const blockInspectorShortcuts = () => {
  window.addEventListener('contextmenu', (e) => e.preventDefault());
  window.addEventListener('keydown', (e) => {
    const ctrl = e.ctrlKey || e.metaKey;
    const isF12 = e.key === 'F12';
    const isInspect = ctrl && e.shiftKey && ['I', 'J', 'C'].includes((e.key || '').toUpperCase());
    const isViewSource = ctrl && !e.shiftKey && (e.key || '').toUpperCase() === 'U';
    if (isF12 || isInspect || isViewSource) {
      e.preventDefault();
      e.stopPropagation();
    }
  });
};

export default function disableDevToolsInProduction() {
  if (!isProduction() || !isTopWindow()) return;
  disableReactDevToolsHook();
  silenceConsole();
  blockInspectorShortcuts();
}
